import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Crown, Gift, RefreshCw, Trophy } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { staffApiService } from '../services/staffApi';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { prefetchDiscordPlayers } from '../hooks/useDiscordPlayer';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { StaffAvatar } from '../components/staff/StaffAvatar';
import { StaffSection } from '../components/staff/StaffSection';
import { StaffHonourBadge } from '../components/staff/StaffHonourBadge';
import { HubStat, StaffHubHero, StaffHubNav } from '../components/staff/StaffHubNav';
import type { StaffTicketCountsResponse, StaffTicketPerson } from '../types/staff';
import { getStaffPerms, STAFF_PANEL_PERMISSION } from '../utils/staffPermissions';
import {
  currentTicketMonth,
  describeRaffleStanding,
  entriesLabel,
  formatTicketMonth,
  latestFinalizedMonth,
  monthHonours,
  monthTicketTotal,
  rankTicketMonth,
  ticketRoleLabel,
} from '../utils/staffTickets';
import { cn } from '../utils/cn';

/** Raffle standing drops out below md so the leaderboard fits without sideways scrolling. */
const COL_RAFFLE = 'hidden md:table-cell';
const TH = 'py-2.5 text-[11px] uppercase tracking-[0.14em] text-glass-500 font-semibold whitespace-nowrap';

const ROLE_TONE: Record<string, string> = {
  administrator: 'bg-red-500/15 text-red-300 border-red-500/25',
  moderator: 'bg-blue-500/15 text-blue-300 border-blue-500/25',
  helper: 'bg-green-500/15 text-green-300 border-green-500/25',
  bot: 'bg-glass-200/10 text-glass-300 border-glass-200/20',
};

const RolePill = ({ role }: { role: StaffTicketPerson['role'] }) => (
  <span className={cn('inline-flex items-center h-4 px-1.5 rounded text-[9px] font-semibold uppercase tracking-[0.08em] border whitespace-nowrap', ROLE_TONE[role ?? ''] ?? 'bg-glass-100/5 text-glass-500 border-glass-200/15')}>
    {ticketRoleLabel(role)}
  </span>
);

/** Avatar + name link for an honours card; falls back to the raw Discord ID when the API did not list the person. */
const PersonLink = ({ person, id }: { person: StaffTicketPerson | null; id: string | null }) => {
  const discordId = person?.discord_id ?? id;
  if (!discordId) return <span className="text-sm text-glass-500">—</span>;
  const name = person?.name ?? discordId;
  return (
    <Link to={`/staff/${discordId}`} className="inline-flex items-center gap-2 min-w-0 group">
      <StaffAvatar discordId={discordId} name={name} size="sm" />
      <span className="text-sm font-semibold text-white truncate group-hover:text-accent-300 transition-colors">{name}</span>
    </Link>
  );
};

/** Staff Manager · Tickets — tickets closed per member for six months, each finalized month's MVP and raffle winner. */
export const StaffTicketsView = () => {
  useDocumentTitle('Staff Tickets');
  const navigate = useNavigate();
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const perms = useMemo(() => getStaffPerms(identity, isDev), [identity, isDev]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState<StaffTicketCountsResponse | null>(() => staffApiService.getCachedTicketCounts());
  const [loading, setLoading] = useState(data === null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (force = false) => {
    if (force) setRefreshing(true);
    setError(null);
    try {
      const res = await staffApiService.getTicketCounts({ force });
      setData(res);
      void prefetchDiscordPlayers(res.staff.map((s) => s.discord_id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load ticket counts');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (perms.canView) void load();
  }, [perms.canView, load]);

  const current = data ? currentTicketMonth(data) : null;
  const requested = searchParams.get('month');
  const selectedMonth = data && requested && data.months.some((m) => m.month === requested) ? requested : current?.month ?? '';
  const selected = data?.months.find((m) => m.month === selectedMonth) ?? null;
  const rows = useMemo(() => (data && selectedMonth ? rankTicketMonth(data, selectedMonth) : []), [data, selectedMonth]);
  const honours = useMemo(() => (data ? monthHonours(data) : []), [data]);
  const stats = useMemo(() => {
    if (!data || !current) return null;
    const leader = rankTicketMonth(data, current.month).find((r) => r.rank === 1) ?? null;
    const last = latestFinalizedMonth(data);
    const lastHonours = last ? honours.find((h) => h.month.month === last.month) ?? null : null;
    return { total: monthTicketTotal(data, current.month), leader, last, lastHonours };
  }, [data, current, honours]);

  const selectMonth = (month: string) => {
    const next = new URLSearchParams(searchParams);
    if (month === current?.month) next.delete('month');
    else next.set('month', month);
    setSearchParams(next, { replace: true });
  };

  if (!perms.canView) {
    return (
      <div className="glass-card p-8 text-center">
        <p className="text-glass-300">You don't have permission to view the Staff Manager.</p>
        <p className="text-glass-500 text-sm mt-1">
          Ask an admin for the <code>{STAFF_PANEL_PERMISSION}</code> permission.
        </p>
      </div>
    );
  }

  const ranked = rows.filter((r) => r.rank !== null);
  const maxTickets = Math.max(1, ...ranked.map((r) => r.standing.tickets));

  const monthPicker = data && (
    <div className="inline-flex flex-wrap rounded-lg border border-glass-200/20 p-0.5 text-[11px] font-mono" role="group" aria-label="Month">
      {data.months.map((m) => {
        const active = m.month === selectedMonth;
        return (
          <button
            key={m.month}
            type="button"
            onClick={() => selectMonth(m.month)}
            aria-pressed={active}
            title={`${formatTicketMonth(m.month, 'long')}${m.finalized ? '' : ' · in progress'}`}
            className={cn('px-2.5 h-6 rounded-md transition-colors whitespace-nowrap touch-manipulation', active ? 'bg-accent-500/20 text-accent-300' : 'text-glass-400 hover:text-white')}
          >
            {formatTicketMonth(m.month, 'name')}
            {!m.finalized && <span className="ml-1 text-[9px] uppercase tracking-wide text-green-300">live</span>}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6">
      <StaffHubHero
        title="Ticket Leaderboard"
        icon={Trophy}
        blurb="Tickets closed per staff member this month and the five before it — the numbers behind /ticketcount. Each finalized month names an MVP (most tickets) and a raffle winner drawn from everyone else's entries."
        nav={<StaffHubNav perms={perms} />}
        actions={
          <button type="button" onClick={() => void load(true)} className="glass-button-secondary inline-flex items-center gap-2 text-sm" disabled={refreshing || loading}>
            <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
            Refresh
          </button>
        }
        stats={
          stats && current ? (
            <>
              <HubStat label="This month" value={stats.total} sub={`tickets closed · ${formatTicketMonth(current.month)}`} />
              <HubStat label="Leading" value={stats.leader?.person.name ?? '—'} sub={stats.leader ? `${stats.leader.standing.tickets} tickets so far` : 'no tickets closed yet'} tone="text-green-300" />
              <HubStat
                label="Last MVP"
                value={stats.lastHonours?.mvp?.name ?? stats.last?.mvp_discord_id ?? '—'}
                sub={stats.last ? `${formatTicketMonth(stats.last.month)} · ${stats.last.mvp_tickets ?? 0} tickets` : 'no finalized month yet'}
                tone="text-amber-300"
              />
              <HubStat
                label="Last raffle winner"
                value={stats.lastHonours?.raffleWinner?.name ?? stats.last?.raffle_winner_id ?? '—'}
                sub={stats.last ? `${formatTicketMonth(stats.last.month)} · ${entriesLabel(stats.last.raffle_winner_entries ?? 0)} of ${stats.last.entries_total ?? 0}` : 'no draw yet'}
                tone="text-purple-300"
              />
            </>
          ) : undefined
        }
      />

      {error && !data && !loading && (
        <div className="glass-card p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Failed to load ticket counts</h2>
          <p className="text-glass-400">{error}</p>
          <button onClick={() => void load(true)} className="glass-button mt-4">
            Retry
          </button>
        </div>
      )}
      {error && data && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100 flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5 text-amber-300" /> Could not refresh: {error}. Showing the last snapshot.
        </div>
      )}

      {loading && (
        <div className="glass-card p-6">
          <SkeletonLoader className="h-10 w-full mb-4" />
          {Array.from({ length: 8 }, (_, i) => (
            <SkeletonLoader key={i} className="h-12 w-full mb-2" />
          ))}
        </div>
      )}

      {data && selected && (
        <StaffSection title={`Leaderboard · ${formatTicketMonth(selected.month)}`} icon={Trophy} count={ranked.length} actions={monthPicker} bodyClassName="px-0 md:px-0 pb-0 md:pb-0">
          <p className="px-4 md:px-5 pb-3 text-[11px] text-glass-500">
            {selected.finalized
              ? `Finalized${selected.source === 'seed_file' ? ' (manually corrected)' : ''} · ${selected.entrants_count ?? 0} entrants · ${entriesLabel(selected.entries_total ?? 0)} in the draw`
              : 'In progress — the MVP and the raffle are decided when the month is finalized.'}
          </p>
          {rows.length === 0 ? (
            <p className="px-4 md:px-5 pb-4 text-sm text-glass-500">No ticket records for this month.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <thead>
                  <tr className="border-b border-glass-200/15">
                    <th className={cn(TH, 'w-12 px-3 text-center')}>#</th>
                    <th className={cn(TH, 'px-4 text-left')}>Member</th>
                    <th className={cn(TH, 'w-[200px] px-4 text-left')}>Tickets</th>
                    {selected.finalized && <th className={cn(TH, 'w-[220px] px-4 text-left', COL_RAFFLE)}>Raffle</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ person, standing, rank }) => {
                    const isBot = person.role === 'bot';
                    const width = `${Math.min(100, Math.max(standing.tickets > 0 ? 4 : 0, (standing.tickets / maxTickets) * 100))}%`;
                    return (
                      <tr
                        key={person.discord_id}
                        onClick={() => navigate(`/staff/${person.discord_id}`)}
                        className={cn('group border-b border-glass-200/10 transition-colors hover:bg-accent-500/5 cursor-pointer', (standing.tickets === 0 || isBot) && 'opacity-70')}
                      >
                        <td className="px-3 py-2.5 text-center">
                          {rank === null ? (
                            <span className="text-xs text-glass-500">—</span>
                          ) : (
                            <span className={cn('font-mono tabular-nums text-sm', rank === 1 ? 'text-amber-300 font-bold' : rank <= 3 ? 'text-white font-semibold' : 'text-glass-400')}>{rank}</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-3 min-w-0">
                            <StaffAvatar discordId={person.discord_id} name={person.name} size="sm" />
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <span className="text-sm font-semibold text-white truncate group-hover:text-accent-300 transition-colors">{person.name}</span>
                                {standing.is_mvp && <Crown className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" aria-label="MVP" />}
                                {standing.is_raffle_winner && <Gift className="w-3.5 h-3.5 text-purple-300 flex-shrink-0" aria-label="Raffle winner" />}
                              </div>
                              <div className="flex items-center gap-1.5 text-[11px] font-mono text-glass-500 min-w-0">
                                <span className="truncate">@{person.username}</span>
                                <RolePill role={person.role} />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className="w-9 text-right text-sm font-semibold tabular-nums text-white">{standing.tickets}</span>
                            <span className="flex-1 h-1.5 rounded-full bg-glass-100/10 overflow-hidden">
                              <span className={cn('block h-full rounded-full', isBot ? 'bg-glass-300/40' : 'bg-gradient-to-r from-accent-500 to-accent-300')} style={{ width }} />
                            </span>
                          </div>
                        </td>
                        {selected.finalized && (
                          <td className={cn('px-4 py-2.5 text-xs', COL_RAFFLE)}>
                            {standing.is_mvp || standing.is_raffle_winner ? (
                              <StaffHonourBadge honour={{ month: selected.month, kind: standing.is_mvp ? 'mvp' : 'raffle', tickets: standing.tickets, entries: standing.entries }} />
                            ) : (
                              <span className={standing.excluded ? 'text-glass-500' : 'text-glass-300'}>{describeRaffleStanding(standing)}</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </StaffSection>
      )}

      {data && honours.length > 0 && (
        <StaffSection title="Monthly honours" icon={Crown} count={honours.length}>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,260px),1fr))] gap-3">
            {honours.map(({ month, mvp, raffleWinner }) => (
              <div key={month.month} className={cn('rounded-lg bg-glass-100/5 border px-3 py-3 space-y-2.5', month.month === selectedMonth ? 'border-accent-400/40' : 'border-glass-200/10')}>
                <div className="flex items-center justify-between gap-2">
                  <button type="button" onClick={() => selectMonth(month.month)} className="text-[11px] font-semibold uppercase tracking-[0.14em] text-glass-400 hover:text-accent-300 transition-colors">
                    {formatTicketMonth(month.month)}
                  </button>
                  <span className="text-[10px] font-mono text-glass-500 tabular-nums whitespace-nowrap">
                    {month.entrants_count ?? 0} entrants · {month.entries_total ?? 0} entries
                  </span>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <Crown className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" aria-label="MVP" />
                  <PersonLink person={mvp} id={month.mvp_discord_id} />
                  <span className="ml-auto text-[11px] font-mono text-glass-500 tabular-nums whitespace-nowrap">{month.mvp_tickets ?? 0} tickets</span>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <Gift className="w-3.5 h-3.5 text-purple-300 flex-shrink-0" aria-label="Raffle winner" />
                  <PersonLink person={raffleWinner} id={month.raffle_winner_id} />
                  <span className="ml-auto text-[11px] font-mono text-glass-500 tabular-nums whitespace-nowrap">{entriesLabel(month.raffle_winner_entries ?? 0)}</span>
                </div>
              </div>
            ))}
          </div>
        </StaffSection>
      )}

      {data && (
        <p className="text-[11px] font-mono text-glass-500 leading-relaxed">
          Snapshot generated {formatDistanceToNow(new Date(data.generated_at * 1000), { addSuffix: true })} · the current month is live from the bot; finalized months come from the raffle's own export. Months with no surviving record are left out rather than counted as zero.
        </p>
      )}
    </div>
  );
};
